import React from 'react';
import classnames from 'classnames';
import { mixColorForTheme } from 'javascripts/theme';
import { HighlightText, SelectroController } from '@aha-app/selectro-v2';
import SelectroV2TagsTriggerTemplate from '../components/SelectroV2/Templates/SelectroV2TagsTriggerTemplate';
import SelectroV2FilterTriggerTemplate from '../components/SelectroV2/Templates/SelectroV2FilterTriggerTemplate';
import { ProjectsMenuV2Templates } from '../components/SelectroV2/Templates/ProjectsMenuV2Template';
import { ApplicationView, useController } from '@aha-app/mvc';
import {
  InitiativesSelectroV2Templates,
  StrategicImperativesSelectroV2Templates,
  UsersSelectroV2Templates,
} from '../components/SelectroV2/Templates/recordTemplates';
import UserAssignToMeAction from '../components/SelectroV2/Templates/Users/UserAssignToMeAction';

export const Templates = {
  Selectro_v2: {
    trigger: {
      addButton: () => {
        return (
          <span className='btn btn-link text-left'>
            <i className='fa-regular fa-circle-plus' />
            Add
          </span>
        );
      },
      // pass in a custom: { colorMap: { value: color } } to color the pills,
      // colors are pallete values like "emerald-200"
      // see Discovery::IdeaUserStudyDecorator::STATUS_COLORS for example
      ahaPill: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        return items.map(item => {
          return (
            <aha-pill color={`var(--aha-${state.custom.colorMap[item.value]})`}>
              {item.text}
            </aha-pill>
          );
        });
      }),
      featureWithRef: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        return (
          <button
            type='button'
            className='selectro-trigger selectro-v2-template--with-ref-trigger'
          >
            {items.length ? (
              items.map(item => {
                return (
                  <div
                    key={item.value || item.data?.value}
                    className='selectro-v2-template--with-ref-trigger--item'
                  >
                    {item.ref || item.data?.ref ? (
                      <>
                        <span className='refnum'>
                          {item.ref || item.data?.ref}
                        </span>{' '}
                      </>
                    ) : null}
                    <span
                      className={classnames(
                        'selectro-v2-template--with-ref-trigger--item--text',
                        { strikethrough: item.data?.wont_do }
                      )}
                    >
                      {item.data?.shipped ? (
                        <i className='fa-regular fa-circle-check shipped mr-1' />
                      ) : null}
                      {item.text || item.data?.text}
                    </span>
                  </div>
                );
              })
            ) : (
              <span style={{ color: 'var(--aha-gray-600)' }}>
                {state.text.noneSelected}
              </span>
            )}
          </button>
        );
      }),
      basicTags: SelectroV2TagsTriggerTemplate,
      projectsMenuV2: ProjectsMenuV2Templates.trigger,
      withIcon: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        const iconInfo = item => {
          return item.prepend?.[0] || item.data?.prepend?.[0] || {};
        };

        return (
          <button type='button' className='selectro-trigger tw-leading-7'>
            {items.length ? (
              items.map(item => {
                return (
                  <div key={item.value || item.data?.value}>
                    {iconInfo(item) ? (
                      <i
                        className={classnames(
                          iconInfo(item).icon_class,
                          'tw-mr-1'
                        )}
                      />
                    ) : null}
                    <span>{item.text || item.data?.text}</span>
                  </div>
                );
              })
            ) : (
              <span className='tw-text-accent-text'>
                {state.text.noneSelected}
              </span>
            )}
          </button>
        );
      }),
      withFont: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        return (
          <button
            type='button'
            className='selectro-trigger selectro-v2-template--with-ref-trigger'
          >
            {items.length ? (
              items.map(item => {
                return (
                  <div
                    className='SelectroV2__text'
                    style={{ fontFamily: item.font ?? item.data.font }}
                  >
                    {item.text}
                  </div>
                );
              })
            ) : (
              <span style={{ color: 'var(--aha-gray-600)' }}>
                {state.text.noneSelected}
              </span>
            )}
          </button>
        );
      }),

      filter: SelectroV2FilterTriggerTemplate,
      documentType: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        return (
          <button
            type='button'
            className='selectro-trigger user-dashboard__selectro'
          >
            Document type:{' '}
            {items.length ? (
              items.map(item => {
                return <span className='SelectroV2__text'>{item.text}</span>;
              })
            ) : (
              <span className='tw-gray-600'>{state.text.noneSelected}</span>
            )}
          </button>
        );
      }),
      sharedBy: ApplicationView(({ items }: { items: any }) => {
        const { state } = useController(SelectroController);
        return (
          <button
            type='button'
            className='selectro-trigger user-dashboard__selectro'
          >
            Shared by:{' '}
            {items.length ? (
              items.map(item => {
                return <span className='SelectroV2__text'>{item.text}</span>;
              })
            ) : (
              <span style={{ color: 'var(--aha-gray-600)' }}>
                {state.text.noneSelected}
              </span>
            )}
          </button>
        );
      }),
    },
    clearConfirmation: {
      default: () => {
        return (
          <>
            <aha-modal-header warning>Are you sure?</aha-modal-header>

            <aha-modal-body-danger>
              <div slot='subheader'>Are you sure?</div>
              <p slot='content'>
                Continuing will unselect all selected records.
              </p>
            </aha-modal-body-danger>
          </>
        );
      },
    },
    item: {
      withStatus: ({ item, tabIndex }) => {
        const itemColor = mixColorForTheme('#' + item.data.color, 0.3);
        const borderColor = mixColorForTheme('#' + item.data.color, 0.02);
        const itemClasses = classnames('SelectroV2__text', 'optionItem');

        return (
          <React.Fragment>
            <span
              className={classnames('fa-stack', 'fa-1x', 'itemIconWrapper')}
            >
              <i
                className='fa-solid fa-square fa-stack-1x'
                style={{ color: itemColor, fontSize: '15px' }}
              />
              <i
                className='fa-regular fa-square fa-stack-1x'
                style={{ color: borderColor, fontSize: '15px' }}
              />
            </span>

            <div
              className={itemClasses}
              data-testid='item-default-template'
              tabIndex={tabIndex}
            >
              {item.text}
            </div>
          </React.Fragment>
        );
      },

      withRef: ({ item, tabIndex }) => {
        return (
          <div className='SelectroV2__text'>
            {item.data.ref ? (
              <>
                <span className='refnum' style={{ marginRight: '1ch' }}>
                  {item.data.ref}
                </span>{' '}
              </>
            ) : null}
            {item.data.shipped ? (
              <i className='fa-regular fa-circle-check shipped mr-1' />
            ) : null}
            <span className={classnames({ strikethrough: item.data.wont_do })}>
              {item.data.text}
            </span>
          </div>
        );
      },
      projectsMenuV2: ProjectsMenuV2Templates.item,
      autocomplete: ({ item, tabIndex }) => {
        return (
          <div
            className='SelectroV2__text selectro-v2-template--autocomplete-item'
            tabIndex={item.disabled ? null : tabIndex}
          >
            <span className={classnames({ strikethrough: item.data.wont_do })}>
              <HighlightText item={item} text={item.data?.text || item.text} />
            </span>
            {item.updatesSearch ? (
              <i className='fa-solid fa-angle-right' />
            ) : null}
          </div>
        );
      },
    },
    itemContent: {
      // This is to be used with selectros that are choosing a person from a list of people. When not searching, you will only see the person's name. When searching, you will see the person's name and email address.
      showUserEmailWhenSearching: ApplicationView(
        ({ item, tabIndex }: { item: any; tabIndex: number }) => {
          const controller = useController(SelectroController);
          return (
            <div
              style={{
                display: 'flex',
                gap: '1ch',
                whiteSpace: 'nowrap',
              }}
            >
              <div>
                <HighlightText item={item} text={item.text} />
              </div>
              {controller.state.searchQuery && item.data.email ? (
                <div>
                  (<HighlightText item={item} text={item.data.email} />)
                </div>
              ) : null}
            </div>
          );
        }
      ),
      withPermissionWarning: ApplicationView(({ item }: { item: any }) => {
        const controller = useController(SelectroController);
        if (!item.data.user_has_permission) {
          item.onClick = e => {
            e.preventDefault();

            // eslint-disable-next-line no-unused-expressions
            e.target.querySelector(':scope aha-modal')?.show();
          };
        }

        return (
          <>
            <HighlightText item={item} text={item.text} />
            <aha-modal size='medium'>
              <aha-modal-header modal-title='Warning' />
              <aha-modal-body-danger>
                Are you sure you want to move this record?
                <p slot='content'>
                  You are moving this record to a workspace in which you do not
                  have permissions to edit records. If you proceed, you will
                  lose the ability to edit this record.
                </p>
              </aha-modal-body-danger>
              <aha-modal-footer>
                <aha-button
                  slot='secondary_actions'
                  onClick={e => {
                    if (e.target instanceof Element) {
                      void e.target.closest('aha-modal')?.hide();
                    }
                  }}
                >
                  Cancel
                </aha-button>
                <aha-button
                  kind='danger'
                  onClick={() => controller.selectItem({ item })}
                >
                  Continue
                </aha-button>
              </aha-modal-footer>
            </aha-modal>
          </>
        );
      }),
      withFont: ({ item, tabIndex }) => {
        return (
          <div
            className='SelectroV2__text'
            style={{ fontFamily: item.data.font }}
            tabIndex={tabIndex}
          >
            {item.text}
          </div>
        );
      },
    },
    itemPostpend: {
      itemWithCount: ({ item }) => {
        return (
          <span className='badge' style={{ height: 'min-content' }}>
            {item.data.count}
          </span>
        );
      },
      itemWithIcon: ({ item }) => {
        if (item.data.postpend) {
          const { icon_title, icon_class } = item.data.postpend;

          return (
            <div style={{ minWidth: '20px', textAlign: 'right' }}>
              <i
                className={icon_class}
                title={icon_title}
                style={{ color: 'var(--icon-color)' }}
              />
            </div>
          );
        } else {
          return null;
        }
      },
    },
    itemPrepend: {
      itemWithIcon: ({ item }) => {
        if (item.data.prepend) {
          let icon_title, icon_class;

          // selectrov1 used an array data structure here, so we can support
          // that or the new way of jusing just an object.
          if (Array.isArray(item.data.prepend)) {
            icon_title = item.data.prepend[0].icon_title;
            icon_class = item.data.prepend[0].icon_class;
          } else {
            icon_title = item.data.prepend.icon_title;
            icon_class = item.data.prepend.icon_class;
          }

          return (
            <i
              title={icon_title}
              className={icon_class}
              style={{ marginRight: '0.3em', position: 'relative' }}
            />
          );
        } else {
          return null;
        }
      },
    },
    initiatives: InitiativesSelectroV2Templates,
    strategicImperatives: StrategicImperativesSelectroV2Templates,
    users: {
      ...UsersSelectroV2Templates,
      // This template expects to have the current_user in the selectro_presenter format
      // in state.custom.currentUser, to add that use the "custom" prop.
      assignToMeAction: UserAssignToMeAction,
    },
  },
};

window.Templates = Templates;
